/** The analysis layers of a design version, fetched once when a layer first needs them and decoded to typed
 * arrays (the API caches the computation per version; the browser keeps the response for an hour). */
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { api, unwrap, type S } from "../../api/client";
import type { Vec2 } from "../geo.ts";
import { decodeRaster, meanInPolygon, type EncodedRaster, type GridSpec, type Raster } from "./grid.ts";

export interface AnalysisData {
  version: number;
  heightAboveGround: Raster | null;
  surface: Raster | null;
  groundMethod: string;
  annual: Raster | null;
  access: Raster | null;
  tsrf: Raster | null;
  /** Twelve months (January first). */
  monthly: Raster[] | null;
  sun: S["AnalysisSun"];
  faces: Map<string, S["AnalysisFace"]>;
}

export function decodeLayers(body: S["AnalysisLayers"]): AnalysisData {
  const site = body.site.grid as GridSpec;
  const roof = body.roof.grid as GridSpec;
  const raster = (layer: unknown, grid: GridSpec) => decodeRaster(layer as EncodedRaster | null, grid);
  const monthly = body.monthly
    ? body.monthly.months.map((m) => raster(m, body.monthly!.grid as GridSpec)).filter((r): r is Raster => r != null)
    : null;
  return {
    version: body.version_number,
    heightAboveGround: raster(body.site.height, site),
    surface: raster(body.site.surface, site),
    groundMethod: body.site.ground_method,
    annual: raster(body.roof.annual_flux, roof),
    access: raster(body.roof.solar_access, roof),
    tsrf: raster(body.roof.tsrf, roof),
    monthly: monthly && monthly.length === 12 ? monthly : null,
    sun: body.sun,
    faces: new Map(body.faces.map((f) => [f.face_id, f])),
  };
}

export function useAnalysisData(designId: string, version: number, enabled: boolean) {
  const query = useQuery({
    queryKey: ["analysis-layers", designId, version],
    queryFn: () =>
      unwrap(
        api.GET("/v1/designs/{design_id}/versions/{version_number}/analysis/layers", {
          params: { path: { design_id: designId, version_number: version } },
        }),
      ),
    enabled,
    staleTime: Infinity,
    gcTime: 30 * 60 * 1000,
    retry: 1,
  });
  const data = useMemo(() => (query.data ? decodeLayers(query.data) : null), [query.data]);
  return { data, loading: query.isLoading && enabled, error: query.error };
}

/** A panel's numbers from the layers: its mean solar access and TSRF, and its yield (kWh/kW) under it. */
export function panelStats(data: AnalysisData, plan: Vec2[]): { access: number; tsrf: number; kwhPerKw: number } {
  return {
    access: data.access ? meanInPolygon(data.access, plan) : NaN,
    tsrf: data.tsrf ? meanInPolygon(data.tsrf, plan) : NaN,
    kwhPerKw: data.annual ? meanInPolygon(data.annual, plan) : NaN,
  };
}
